import type { ClinicModuleState } from '@/shared/api/clinicModulesClient'

/**
 * THE THREE STATES AND THE FOUR ACTS, as pure decisions.
 *
 * The lifecycle (tracker #198) has exactly three effective states —
 * enabled-visible, disabled-visible, disabled-hidden — and `enabled=true,
 * hidden=true` is refused at every boundary. This module is the page's whole
 * reading of that table: what one status word a state earns, which act each
 * of the two switches performs from it, and what desired state an act asks
 * the server for. Kept apart from the components so the table is provable
 * without rendering, and so a switch cannot grow a private reading of it.
 *
 * "Hidden wins" everywhere: a state that somehow arrives enabled AND hidden
 * reads as hidden, because hidden is the state whose consequences are wider,
 * and the enabled switch stands off — the server would refuse the enable
 * anyway.
 */
export type ClinicModuleStatus = 'enabled' | 'disabled' | 'hidden'

export type ClinicModuleAct = 'enable' | 'disable' | 'hide' | 'unhide'

export const CLINIC_MODULE_ACTS: readonly ClinicModuleAct[] = ['enable', 'disable', 'hide', 'unhide']

export function moduleStatus(state: ClinicModuleState): ClinicModuleStatus {
  if (state.hidden) return 'hidden'
  return state.enabled ? 'enabled' : 'disabled'
}

/**
 * What the Enabled switch does from this state, or `null` when it does
 * nothing: a hidden module cannot be turned on, only shown again first.
 */
export function enabledSwitchAct(state: ClinicModuleState): 'enable' | 'disable' | null {
  if (state.hidden) return null
  return state.enabled ? 'disable' : 'enable'
}

/** What the Hidden switch does from this state. Always something. */
export function hiddenSwitchAct(state: ClinicModuleState): 'hide' | 'unhide' {
  return state.hidden ? 'unhide' : 'hide'
}

/**
 * The state an act asks for. Two acts ask for disabled-visible — turning a
 * visible module off and showing a hidden one again — and that is the point:
 * unhide never asks for enabled, so one confirmation can never carry both
 * transitions. Enabled-hidden is not a row here and cannot be produced.
 */
export function desiredState(act: ClinicModuleAct): { enabled: boolean; hidden: boolean } {
  switch (act) {
    case 'enable':
      return { enabled: true, hidden: false }
    case 'disable':
    case 'unhide':
      return { enabled: false, hidden: false }
    case 'hide':
      return { enabled: false, hidden: true }
  }
}

/**
 * Whether the act is still the one the switch would perform from a state —
 * the check a retry runs before re-sending, so that a desired act outlived by
 * the state (someone else hid the module while this operator was deciding to
 * turn it off) is refused here rather than turned into a different transition.
 */
export function actAppliesTo(act: ClinicModuleAct, state: ClinicModuleState): boolean {
  return enabledSwitchAct(state) === act || hiddenSwitchAct(state) === act
}
